import React, {Component} from 'react';
import {graphql} from 'react-apollo';
import {autobind} from 'core-decorators';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import userQuery from 'graphql/queries/user.gql';
import {formatMoney, checkPermission} from 'core/utils';
import {reverse} from 'core/urls';
import {Input} from 'components/forms';
import styles from './styles.css';

@graphql(userQuery)
@autobind
export default class OrderProductTable extends Component {
    static propTypes = {
        items: PropTypes.array.isRequired,
        onUpdate: PropTypes.func,
        readOnly: PropTypes.bool,
        columns: PropTypes.arrayOf(PropTypes.oneOf([
            'product', 'code', 'instructions', 'price', 'quantity', 'total',
        ])).isRequired,
    };

    static contextTypes = {
        router: PropTypes.object,
    };

    constructor(props) {
        super(props);
        this.state = {quantities: this.getQuantities(props.items)};
        this.timeouts = {};
    }

    componentWillReceiveProps(nextProps) {
        if (this.props.items !== nextProps.items) {
            this.setState({quantities: this.getQuantities(nextProps.items)});
        }
    }

    componentWillUnmount() {
        Object.values(this.timeouts).forEach(clearTimeout);
    }

    getQuantities(items) {
        return items.reduce((quantities, item) => ({
            ...quantities,
            [item.id]: item.quantity,
        }), {});
    }

    isClickable(item) {
        const {user} = this.props.data;
        const {productVariation} = item;
        if (!productVariation) return false;
        if (productVariation.product.isRestricted) return checkPermission(user, 'enable_restricted_products');
        return true;
    }

    getReference(item) {
        return ['productVariation', 'bag', 'sign']
            .map(key => item[key])
            .find(Boolean);
    }

    updateQuantityInState(item, quantity) {
        this.setState({quantities: {...this.state.quantities, [item.id]: quantity}});
    }

    updateQuantityInAPI(item, quantity) {
        this.props.onUpdate({
            quantity,
            item: item.id,
        });
    }

    handleItemRemove(item) {
        return () => (
            this.props.onUpdate({
                quantity: 0,
                item: item.id,
            })
        );
    }

    handleItemUpdate(item) {
        return quantity => {
            if (quantity == 0) return;
            this.updateQuantityInState(item, quantity);
            clearTimeout(this.timeouts[item.id]);
            if (!quantity.length) return;
            this.timeouts[item.id] = setTimeout(() => this.updateQuantityInAPI(item, quantity), 750);
        };
    }

    handleItemBlur(item) {
        return () => {
            const newQuantity = this.state.quantities[item.id];
            if (!newQuantity.length) {
                this.updateQuantityInState(item, item.quantity);
            } else if (newQuantity !== item.quantity) {
                clearTimeout(this.timeouts[item.id]);
                this.updateQuantityInAPI(item, newQuantity);
            }
        };
    }

    handleProductClick(item) {
        if (!this.isClickable(item)) return;
        const url = reverse('productDetail', {id: item.productVariation.product.id});
        return () => this.context.router.history.push(url);
    }

    renderReadOnly() {
        return (
            <table className={styles.root}>
                <colgroup>
                    {this.props.columns.map(key => <col key={key} className={styles.columns} />)}
                </colgroup>
                <thead className={styles.header}>
                    <tr>
                        {this.props.columns.includes('product') && <th>Producto</th>}
                        {this.props.columns.includes('code') && <th>Código</th>}
                        {this.props.columns.includes('instructions') && <th>Manual</th>}
                        {this.props.columns.includes('price') && <th>Precio</th>}
                        {this.props.columns.includes('quantity') && <th>Cantidad</th>}
                        {this.props.columns.includes('total') && <th>Total</th>}
                    </tr>
                </thead>
                <tbody className={styles.body}>
                    {this.props.items.map(item => {
                        const reference = this.getReference(item);
                        return (
                            <tr key={item.id} className={classNames({[styles.clickable]: this.isClickable(item)})}>
                                <td>
                                    <div
                                        className={styles.product}
                                        onClick={this.handleProductClick(item)}>
                                        <img src={item.thumbnail} />
                                        <div className={styles.description}>
                                            <span className={styles.itemName}>{item.name}</span>
                                            {item.description && <span>{item.description}</span>}
                                        </div>
                                    </div>
                                </td>
                                <td><span className={styles.mobileOnly}>Código:</span>{item.code}</td>
                                {this.props.columns.includes('instructions') && (
                                    <td>
                                        {reference && reference.instructions && (
                                            <a target='_blank' href={reference.instructions}>Link</a>
                                        )}
                                    </td>
                                )}
                                {this.props.columns.includes('price') && (
                                    <td>
                                        <span className={styles.mobileOnly}>Precio por unidad:</span>
                                        {formatMoney(item, 'price')}
                                    </td>
                                )}
                                <td><span className={styles.mobileOnly}>Cantidad:</span>{item.quantity}</td>
                                {this.props.columns.includes('total') && (
                                    <td>
                                        <span className={styles.mobileOnly}>Total:</span>
                                        {formatMoney(item, 'total')}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        );
    }

    renderEditable() {
        return (
            <table className={styles.root}>
                <colgroup>
                    {this.props.columns.map(key => <col key={key} className={styles.columns} />)}
                    <col className={classNames(styles.columns, styles.removeItem)} />
                </colgroup>
                <thead className={styles.header}>
                    <tr>
                        {this.props.columns.includes('product') && <th>Producto</th>}
                        {this.props.columns.includes('code') && <th>Código</th>}
                        {this.props.columns.includes('instructions') && <th>Manual</th>}
                        {this.props.columns.includes('price') && <th>Precio</th>}
                        {this.props.columns.includes('quantity') && <th>Cantidad</th>}
                        {this.props.columns.includes('total') && <th>Total</th>}
                    </tr>
                </thead>
                <tbody className={styles.body}>
                    {this.props.items.map(item => {
                        const reference = this.getReference(item);
                        const isPreOrder = item.productVariation && item.productVariation.product && item.productVariation.product.isPreOrder || false;
                        return (
                            <tr key={item.id} className={styles.clickable}>
                                <td>
                                    <div
                                        className={styles.product}
                                        onClick={this.handleProductClick(item)}>
                                        <img src={item.thumbnail} />
                                        <div className={styles.description}>
                                            <span className={styles.itemName}>{item.name} {isPreOrder && <span> - a reservar</span>} </span>
                                            {item.description && <span>{item.description}</span>}
                                            {isPreOrder && <p className={styles.preOrder}> Fecha de entrega a confirmar</p>}
                                        </div>
                                    </div>
                                </td>
                                <td><span className={styles.mobileOnly}>Código:</span>{item.code}</td>
                                {this.props.columns.includes('instructions') && (
                                    <td>
                                        {reference.instructions && (
                                            <a target='_blank' href={reference.instructions}>Link</a>
                                        )}
                                    </td>
                                )}
                                {this.props.columns.includes('price') && (
                                    <td>
                                        <span className={styles.mobileOnly}>Precio por unidad:</span>
                                        {formatMoney(item, 'price')}
                                    </td>
                                )}
                                <td>
                                    <span className={styles.mobileOnly}>Cantidad:</span>
                                    <Input
                                        onChange={this.handleItemUpdate(item)}
                                        onBlur={this.handleItemBlur(item)}
                                        type='number'
                                        min={1}
                                        disabled={this.props.disabled}
                                        value={this.state.quantities[item.id]}
                                    />
                                </td>
                                {this.props.columns.includes('total') && (
                                    <td>
                                        <span className={styles.mobileOnly}>Total:</span>
                                        {formatMoney(item, 'total')}
                                    </td>
                                )}
                            </tr>
                        );
                    })}
                    {(this.props.total !== undefined && !this.props.hideTotal) && (
                        <tr className={styles.total}>
                            <td colSpan={4}>TOTAL</td>
                            <td>{formatMoney(this.props, 'total')}</td>
                        </tr>
                    )}
                </tbody>
            </table>
        );
    }

    render() {
        return this.props.readOnly ? this.renderReadOnly() : this.renderEditable();
    }
}



// WEBPACK FOOTER //
// ./components/order-product-table/index.js